import { type ComponentProps, memo, useMemo } from 'react';
import { type MediaDeviceFailure } from 'livekit-client';
import { LiveKitRoom } from '@livekit/components-react';
import { type AnyObject } from '@Types';
import clsx from 'clsx';
import { useSuspenseDynamicConfig } from 'FrontRoyalStatsig';
import { type AudioUiContext } from 'TutorBotConversation';
import { useConnectionDetails } from '../hooks/useConnectionDetails';
import {
    type TurnDetectionType,
    type Eagerness,
    type SemanticVadOptions,
    type SupportedColors,
    type TokenAttributes,
} from '../TutorBotVoiceAgent.types';
import { ConnectedLiveKitRoomUi } from './ConnectedLiveKitRoomUi';
import { useStoreConversationContext } from '../hooks/useStoreConversationContext';
import { useLogLaunchEvent } from '../hooks/useLogLaunchEvent';
import { type SessionWillEndSoonMessageKey } from './SessionWillEndSoonMessage';
import { MAX_WIDTH_CLASS } from '../constants';

const defaultTurnDetection: SemanticVadOptions = {
    turnDetectionType: 'semantic_vad',
    eagerness: 'auto',
} as const;

type Props = {
    agentIdentifier: string;
    uiContext: AudioUiContext;
    extraTokenAttributes?: AnyObject;

    // "Aborting" means not only disconnecting the conversation but closing the VoiceAgentRoom
    // wrapper (i.e. we're not going back to the intro screen. We're leaving the voice experience
    // altogether).
    // When "allowPostDiscussion" is false, the only way to leave the conversation is to abort.
    // There are different ways that may be available for the user to abort:
    // 1. the BlockLink at the bottom of the StartScreen. This is always available
    // 2. the BlockLink at the bottom of the conversation UI. This is only available when allowPostDiscussion is true, because
    //      in that situation the close button on the audio control bar ends the conversation and returns to the intro screen, while the
    //      BlockLink aborts, triggering abortFromRoom
    // 3. the close button in the audio bar control bar. This button only aborts when allowPostDiscussion
    //      is false (when allowPostDiscussion is true, the close button goes to PostDiscussion). When
    //      allowPostDiscussion is false:
    //    1. We hide the BlockLink, since it would do the same thing as the close button anyway
    //    2. We show a confirm modal before aborting, since it's unclear what that button would do
    abortFromRoom: (() => void) | null;
    abortText: string;
    allowPostDiscussion: boolean;

    // the confirm props are optional, because they're unneeded when allowPostDiscussion is true. They should
    // be required, though, when allowPostDiscussion is false. Ideally we would enforce that with a
    // discriminated union, but due to the way we're using these props to build the props for VoiceAgentRoom,
    // we can't do that.  ConnectedLiveKitRoomUi will error at runtime if it's needed and we don't have any of them.
    // abortFromRoom is essentially the same. It's allowed to be null only when allowPostDiscussion is true.
    abortConfirmText?: string | null;
    abortConfirmContinueButtonText?: string | null;
    abortConfirmCancelButtonText?: string | null;
    abortConfirmHeader?: string | null;

    conversationId: string;
    onDisconnected: () => void;
    color: SupportedColors | undefined;
    sessionWillEndSoonMessageKey?: SessionWillEndSoonMessageKey;
    onDataReceived?: ComponentProps<typeof ConnectedLiveKitRoomUi>['onDataReceived'];
};

function useDynamicConfigTokenAttributeOverrides() {
    const { turnDetectionType, eagerness, threshold, prefixPaddingMs, silenceDurationMs } = useSuspenseDynamicConfig<{
        turnDetectionType?: TurnDetectionType;
        eagerness?: Eagerness;
        threshold?: number;
        prefixPaddingMs?: number;
        silenceDurationMs?: number;
    }>('tutor_bot_voice_agent');

    const configOverrides = useMemo(() => {
        let overrides: AnyObject = {
            turnDetectionType,
            eagerness,
            threshold,
            prefixPaddingMs,
            silenceDurationMs,
        };
        // remove undefined values
        overrides = Object.fromEntries(Object.entries(overrides).filter(([_, value]) => value !== undefined));

        return overrides;
    }, [turnDetectionType, eagerness, threshold, prefixPaddingMs, silenceDurationMs]);

    return configOverrides;
}

function LiveKitRoomConversationComponent({
    agentIdentifier,
    extraTokenAttributes,
    uiContext,
    conversationId,
    onDisconnected,
    color,
    abortFromRoom,
    allowPostDiscussion,
    abortConfirmHeader,
    abortConfirmText,
    abortConfirmContinueButtonText,
    abortConfirmCancelButtonText,
    abortText,
    sessionWillEndSoonMessageKey,
    onDataReceived,
}: Props) {
    const dynamicConfigOverrides = useDynamicConfigTokenAttributeOverrides();

    const tokenAttributes: TokenAttributes = useMemo(
        () => ({ agentIdentifier, ...defaultTurnDetection, ...dynamicConfigOverrides, ...extraTokenAttributes }),
        [agentIdentifier, extraTokenAttributes, dynamicConfigOverrides],
    );

    useLogLaunchEvent({
        eventType: 'tutorbot:started_voice_agent_conversation',
        agentIdentifier,
        uiContext,
        extraTokenAttributes,
    });

    const { connectionDetails } = useConnectionDetails({
        tokenAttributes,
    });

    // conversationContext is just passed through to the server and stored in tutor_bot_conversations.
    // Adding all of the tokenAttributes gives us all of the context information that the agent has
    // for managing the conversation
    useStoreConversationContext({ conversationId, conversationContext: tokenAttributes });

    return (
        <div className={clsx(MAX_WIDTH_CLASS, 'h-full content-center')}>
            <LiveKitRoom
                token={connectionDetails?.participantToken}
                serverUrl={connectionDetails?.serverUrl}
                connect={!!connectionDetails}
                audio
                video={false}
                onMediaDeviceFailure={onDeviceFailure}
                onDisconnected={onDisconnected}
                className="items-between flex h-full flex-col"
            >
                <ConnectedLiveKitRoomUi
                    conversationId={conversationId}
                    uiContext={uiContext}
                    color={color}
                    abortFromRoom={abortFromRoom}
                    allowPostDiscussion={allowPostDiscussion}
                    abortConfirmHeader={abortConfirmHeader}
                    abortConfirmText={abortConfirmText}
                    abortConfirmContinueButtonText={abortConfirmContinueButtonText}
                    abortConfirmCancelButtonText={abortConfirmCancelButtonText}
                    abortText={abortText}
                    sessionWillEndSoonMessageKey={sessionWillEndSoonMessageKey}
                    onDataReceived={onDataReceived}
                />
            </LiveKitRoom>
        </div>
    );
}

function onDeviceFailure(error?: MediaDeviceFailure) {
    // oxlint-disable-next-line no-console
    console.error(error);
    alert(
        'Error acquiring camera or microphone permissions. Please make sure you grant the necessary permissions in your browser and reload the tab',
    );
}

export const LiveKitRoomConversation = memo(
    LiveKitRoomConversationComponent,
) as typeof LiveKitRoomConversationComponent;
